import qs from 'qs';
import auth from '~/auth';

import type { actions } from '@luxuryescapes/contract-svc-video';

import ApiError from '~/types/ApiError';

import { request } from './common';

export type SearchOutputVideosResponse = actions['searchOutputVideos']['responses'][200]['content']['application/json'];
export type GetTemplateSchemaResponse = actions['getTemplateSchema']['responses'][200]['content']['application/json'];
export type GetTemplateListResponse = actions['getTemplatesList']['responses'][200]['content']['application/json'];
export type SearchAssetsResponse = actions['searchAssets']['responses'][200]['content']['application/json'];
export type CreateVideoResponse = actions['createVideo']['responses'][201]['content']['application/json'];
export type GetOutputVideosSignedUrlsResponse =
  actions['getOutputVideosSignedUrls']['responses'][200]['content']['application/json'];
export type CreateAssetResponse = actions['createAsset']['responses'][201]['content']['application/json'];
export type GetAssetsSignedUrlsResponse =
  actions['getAssetsSignedUrls']['responses'][200]['content']['application/json'];
export type DeleteAssetResponse = actions['deleteAsset']['responses'][200]['content']['application/json'];

export type CreateVideoPayload = actions['createVideo']['parameters']['body']['payload'];
type GetOutputVideosSignedUrlsPayload = actions['getOutputVideosSignedUrls']['parameters']['query'];
type GetAssetsSignedUrlsPayload = actions['getAssetsSignedUrls']['parameters']['query'];
export type AssetMetadata = actions['searchAssets']['responses'][200]['content']['application/json']['result'][number];
export type VideoMetadata =
  actions['searchOutputVideos']['responses'][200]['content']['application/json']['result'][number];

export type AllStatus = 'ACTIVE' | 'DELETED' | 'QUEUED' | 'GENERATING' | 'SUCCEEDED' | 'FAILED';

export type AspectRatio = '16:9' | '4:5' | '9:16';

export interface ResolveSourceUrlResponse {
  status: 200;
  message: string;
  result: { url: string };
}

export interface CreateVideoWithRenderOptions {
  name: string;
  templateName: string;
  templateData: Record<string, unknown>;
  aspectRatio?: AspectRatio;
  startTime?: number;
  audioStartTime?: number;
  cropX?: number;
  cropY?: number;
  cropWidth?: number;
  cropHeight?: number;
}

function basePath() {
  return window.configs.API_HOST + '/api/video/';
}

export async function searchOutputVideos(params: {
  ids?: Array<string>;
  keyword?: string;
  status?: Array<'QUEUED' | 'GENERATING' | 'SUCCEEDED' | 'FAILED' | 'DELETED'>;
  templateName?: string;
  page: number;
  limit: number;
}): Promise<SearchOutputVideosResponse> {
  const queryParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 10,
    ...params,
  };
  return request(`${basePath()}videos/search?${qs.stringify(queryParams)}`, {
    method: 'GET',
  });
}
export async function getTemplateList(): Promise<GetTemplateListResponse> {
  return request(`${basePath()}templates`, {
    method: 'GET',
  });
}

export async function getTemplateSchema(templateName: string): Promise<GetTemplateSchemaResponse> {
  return request(`${basePath()}templates/${templateName}`, {
    method: 'GET',
  });
}

export async function searchAssets(params: {
  ids?: Array<string>;
  offerIds?: Array<string>;
  type?: 'VIDEO' | 'AUDIO';
  status?: Array<'ACTIVE' | 'DELETED'>;
  name?: string;
  page?: number;
  limit?: number;
}): Promise<SearchAssetsResponse> {
  const queryParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 10,
    ...params,
  };
  return request(`${basePath()}assets/search?${qs.stringify(queryParams)}`, {
    method: 'GET',
  });
}

export async function createVideo(payload: CreateVideoPayload): Promise<CreateVideoResponse> {
  return request(`${basePath()}videos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function getOutputVideosSignedUrls(
  payload: GetOutputVideosSignedUrlsPayload,
): Promise<GetOutputVideosSignedUrlsResponse> {
  const queryParams = {
    ...payload,
    videoIds: Array.isArray(payload.videoIds) ? payload.videoIds : [payload.videoIds],
  };
  return request(`${basePath()}videos/signed-urls?${qs.stringify(queryParams)}`, {
    method: 'GET',
  });
}

export async function createAsset(
  file: File,
  offerId?: string,
  name?: string,
  onProgress?: (progress: number) => void,
): Promise<CreateAssetResponse> {
  const token = await auth.getAccessToken();

  if (!token) {
    throw new ApiError({
      message: 'You are not logged in.',
      name: 'AuthorizationError',
    });
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);
    if (offerId) {
      formData.append('offerId', offerId);
    }
    if (name) {
      formData.append('name', name);
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded * 100) / event.total);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.response));
        } catch (e) {
          reject(
            new ApiError({
              message: 'Invalid response format',
              name: 'NetworkError',
              status: xhr.status,
            }),
          );
        }
      } else {
        try {
          const errorResponse = JSON.parse(xhr.response);
          reject(
            new ApiError({
              message: errorResponse.message || `Upload failed with status ${xhr.status}`,
              name: 'NetworkError',
              status: xhr.status,
              errors: errorResponse.errors,
            }),
          );
        } catch (e) {
          reject(
            new ApiError({
              message: `Upload failed with status ${xhr.status}`,
              name: 'NetworkError',
              status: xhr.status,
            }),
          );
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(
        new ApiError({
          message: 'Upload failed',
          name: 'NetworkError',
        }),
      );
    });

    xhr.open('POST', `${basePath()}assets`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
}

export async function getAssetsSignedUrls(payload: GetAssetsSignedUrlsPayload): Promise<GetAssetsSignedUrlsResponse> {
  const queryParams = {
    ...payload,
    assetIds: Array.isArray(payload.assetIds) ? payload.assetIds : [payload.assetIds],
  };
  return request(`${basePath()}assets/signed-urls?${qs.stringify(queryParams)}`, {
    method: 'GET',
  });
}

export async function deleteAsset(id: string): Promise<DeleteAssetResponse> {
  return request(`${basePath()}assets/${id}`, {
    method: 'DELETE',
  });
}

export async function search(
  type: 'assets' | 'videos',
  params: {
    ids?: Array<string>;
    offerIds?: Array<string>;
    status?: Array<string>;
    templateName?: string;
    name?: string;
    page?: number;
    limit?: number;
  },
): Promise<SearchAssetsResponse | SearchOutputVideosResponse> {
  if (type === 'assets') {
    return searchAssets({
      ids: params.ids,
      offerIds: params.offerIds,
      status: params.status as Array<'ACTIVE' | 'DELETED'>,
      name: params.name,
      page: params.page ?? 1,
      limit: params.limit ?? 10,
    });
  } else {
    return searchOutputVideos({
      ids: params.ids,
      keyword: params.offerIds?.length ? params.offerIds.join(',') : undefined,
      status: params.status as Array<'QUEUED' | 'GENERATING' | 'SUCCEEDED' | 'FAILED' | 'DELETED'>,
      templateName: params.templateName,
      page: params.page ?? 1,
      limit: params.limit ?? 10,
    });
  }
}

export async function getSignedUrls(
  type: 'assets' | 'videos',
  ids: Array<string>,
): Promise<GetAssetsSignedUrlsResponse | GetOutputVideosSignedUrlsResponse> {
  if (type === 'assets') {
    return getAssetsSignedUrls({ assetIds: ids });
  } else {
    return getOutputVideosSignedUrls({ videoIds: ids });
  }
}

export async function resolveSourceUrl(videoId: string): Promise<ResolveSourceUrlResponse> {
  return request(`${basePath()}source-url?${qs.stringify({ videoId })}`, {
    method: 'GET',
  });
}

export async function createVideoWithRenderOptions(
  payload: CreateVideoWithRenderOptions,
): Promise<CreateVideoResponse> {
  return request(`${basePath()}videos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
